import React, { useEffect, useMemo, useState } from 'react';
import Table from 'ui-components/table/table';
import { getDriversColumns } from './driversColumn';
import ContentContainer from 'templates/content-container/contentContainer';
import ContentContainerToolbar from 'ui-components/molecules/content-container-toolbar/contentContainerToolbar';
import { PAGE_IDS } from 'app/data/common/pageIds';
import LabeledTextInput from 'ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import Button, { ButtonSizes, ButtonThemes } from 'ui-components/button/button';
import XGSSelect, { XGSSelectOption } from 'ui-components/xgs-select/xgsSelect';
import { useDriversList } from './hooks/useDriversList';
import { useDispatch, useSelector } from 'react-redux';
import { getTerminalsList, terminalSummarySelector } from 'slices/terminal-summary/terminalSummarySlice';
import TerminalSummaryState from 'slices/terminal-summary/TerminalSummaryState';
import DriverDetailsSidebar from './driverDetailsSidebar';
import { setDriver } from 'slices/drivers/driversSlice';
import './drivers.scss';
interface TerminalOption {
  value: string;
  label: string;
}

interface FiltersProps {
  searchString: string;
  setSearchString: (searchString: string) => void;
  terminal: XGSSelectOption | null;
  setTerminal: (terminal: XGSSelectOption | null) => void;
  isLoading: boolean;
  terminalOptions: TerminalOption[];
  setSubmittedSearch: (searchString: string) => void;
}

const Filters = ({
  searchString,
  setSearchString,
  terminal,
  setTerminal,
  isLoading,
  setSubmittedSearch,
  terminalOptions,
}: FiltersProps) => {

  useEffect(() => {
    const handler =
      setTimeout(() =>
        setSubmittedSearch(searchString), 1000);
    return () => clearTimeout(handler);
  }, [searchString, setSubmittedSearch]);

  return (
    <>
      <div className="drivers-list__filter">
        <div className="drivers-list__filter__input">
          <LabeledTextInput
            name="searchString"
            labelMode={LabelModes.column}
            className="drivers-list__filter__input__input"
            value={searchString}
            onChange={(e) =>
              setSearchString(e.currentTarget.value)
            }
            type="text"
            placeholder="Search drivers by name or ID"
            clearable
            onClear={() => { setSearchString(""); setSubmittedSearch("") }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSubmittedSearch(e.currentTarget.value);
              }
            }}
          />
        </div>
        <div className="drivers-list__filter__select">
          <XGSSelect
            name="terminal"
            labelMode={LabelModes.column}
            value={terminal}
            isLoading={isLoading}
            onValueChange={(v: XGSSelectOption | null | undefined) => {
              if (v) {
                setTerminal(v);
              }
            }}
            placeholder="Select Terminal"
            options={terminalOptions}
          />
        </div>
        <div className="drivers-list__filter__button">
          <div className="drivers-list__filter__button__search">
            <Button
              theme={ButtonThemes.blue}
              onClick={() => {
                setSubmittedSearch(searchString);
              }}
              size={ButtonSizes.medium}
            >
              Search
            </Button>
            <Button
              theme={ButtonThemes.gray}
              onClick={() => {
                setSearchString("");
                setTerminal(null);
                setSubmittedSearch("");
              }}
              type="button"
              size={ButtonSizes.medium}
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

const DriversList = () => {
  const dispatch = useDispatch();
  const [searchString, setSearchString] = useState<string>("");
  const [terminal, setTerminal] = useState<XGSSelectOption | null>(null);
  const [terminalOptions, setTerminalOptions] = useState<TerminalOption[]>([]);
  const terminalSummaryState: TerminalSummaryState = useSelector(terminalSummarySelector);
  const [slideOutFilterSidebar, setSlideOutFilterSidebar] = useState<boolean>(false);

  const [submittedSearch, setSubmittedSearch] = useState<string>("");
  const [isEditing, setIsEditing] = useState<boolean>(false);

  const handleEdit = () => {
    setIsEditing(prev => !prev)
  }

  useEffect(() => {
    if (terminalSummaryState.terminallist) {
      const terminalOptions = terminalSummaryState.terminallist?.map((terminal) => ({
        value: terminal.terminalNumber.toString(),
        label: terminal.terminalName.toLowerCase().capitalize()
      })) || [];
      setTerminalOptions(terminalOptions);
    }
  }, [terminalSummaryState.terminallist]);

  useEffect(() => {
    dispatch(getTerminalsList(0, null, null, "ACTIVE", "XGS Terminal", true));
    // eslint-disable-next-line
  }, []);

  const { data: driversData,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
    isFetching,
    isPreviousData } = useDriversList(terminal?.value.toString() || "",
      submittedSearch);

  const data = useMemo(() =>
    driversData?.pages?.flatMap(page => page.data.items) ?? [],
    [driversData]
  );

  const hasMore = !!driversData?.pages?.[driversData.pages.length - 1]?.data?.nextCursor;

  return (
    <ContentContainer
      titleComponent={
        <ContentContainerToolbar pageId={PAGE_IDS.DRIVERLIST}
          title="Drivers"
          tooltip={"This module is in Preview. Certain data and features may be incomplete or under development."}
          status={"PREVIEW"} />
      }
    >
      <Filters
        searchString={searchString}
        setSearchString={setSearchString}
        terminal={terminal}
        setTerminal={setTerminal}
        isLoading={isLoading}
        terminalOptions={terminalOptions}
        setSubmittedSearch={setSubmittedSearch}
      />
      <Table
        infiniteScroll={true}
        data={data}
        rowHeight={58}
        keepTableOnLoading
        infiniteScrollLoading={isFetchingNextPage}
        columns={getDriversColumns()}
        isLoading={isLoading || (isFetching && isPreviousData)}
        sorting={true}
        cursorPointer
        minTableHeight={420}
        onInfiniteScroll={() => fetchNextPage()}
        infiniteScrollHasNext={hasMore}
        interactiveHeader={false}
        onRowClicked={
          (row: any) => {
            setSlideOutFilterSidebar(true);
            setIsEditing(false)
            dispatch(setDriver({ driver: row }));
          }
        }
      />
      <DriverDetailsSidebar
        isOpen={slideOutFilterSidebar}
        isEditing={isEditing}
        handleEdit={handleEdit}
        onClose={() => setSlideOutFilterSidebar(false)}
      />
    </ContentContainer>
  );
};

export default DriversList;
